import { CommonModule } from "@angular/common";
import { ChangeDetectorRef, Component, HostListener, OnChanges, OnDestroy, OnInit, SimpleChanges, effect } from "@angular/core";
import { NavigationEnd, Router, RouterModule } from "@angular/router";
import { Driver, driver } from "driver.js";
import saveAs from 'file-saver';
import { ConfirmationService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { Subscription } from "rxjs";
import { environment } from "../../../../environments/environment.dev";
import { ButtonComponent } from "../../../@core/components/ui/button/button.component";
import { ITabConfig } from "../../../@core/components/ui/tabs";
import { USER_ROLE } from "../../../@core/models/enums";
import { hasBillingAccess } from "../../../@core/utils/permissions.utils";
import { UI_FEATURE } from "../../../@core/models/feature-mapping";
import { IUser, PlanFeature, SHARE_ACCESS, ISubscriptionResponse, PAYMENT_MODE } from "../../../@core/models/model";
import { AuthenticationService } from "../../../@core/services/authentication.service";
import { SubscriptionStateService } from "../../../@core/services/subscription-state.service";
import { FeatureFlagService } from "../../../@core/services/feature-flag.service";
import { ProductWalkThroughService } from "../../../@core/services/product-walk-through.service";
import { ToastService } from "../../../@core/services/toast.service";
import { ExcelUploadDialogComponent, UPLOAD_TYPE } from "../../../@shared/excel-upload-dialog/excel-upload-dialog.component";
import { ISideBarMenu } from "../../../app-layout/models/sidebar.model";
import { SidebarService } from "../../../app-layout/services/sidebar.service";
import { SidebarViewService } from "../../../app-layout/services/sidebar-view.service";
import { SVG } from "../../../common/svgs";
import { SIDEBAR_IDS } from "../../../common/utils";
import { SVG_ICONS } from "../../constants/constant";
import { ChecklistsService } from "../../services/checklists.service";
import { OrganizationSetupFormService } from "../../services/organization-setup-from.service";
import { UserService } from "../../services/user.service";
import { PRODUCT_WALKTHROUGH_TYPE } from "../../models/contract-management.model";
import { ProgressSpinnerModule } from "primeng/progressspinner";


@Component({
    selector: 'app-settings',
    standalone: true,
    imports: [RouterModule, ButtonModule, CommonModule, ExcelUploadDialogComponent, ConfirmDialogModule, ButtonComponent, ProgressSpinnerModule],
    providers: [ConfirmationService],
    templateUrl: './settings.component.html',
    styleUrl: './settings.component.scss'
})


export class SettingsComponent implements OnInit, OnDestroy, OnChanges {

    SVG = SVG;
    settingsSidebarId = SIDEBAR_IDS.SETTINGS;
    sidebarMenu: ISideBarMenu[] = [];

    currentUser: IUser;
    driver !: Driver;
    private routerSubscription!: Subscription;
    private walkThroughSubscription!: Subscription
    uploadExcelDialogVisible = false;
    icons = SVG_ICONS;
    subscriptionDetails: any;
    selectedTab: ITabConfig | null = null;
    url = '';
    ENV = environment;
    setupSidebarDone: Boolean = false;

    constructor(
        private cd: ChangeDetectorRef,
        private router: Router,
        private authService: AuthenticationService,
        private userService: UserService,
        private productWalkThroughService: ProductWalkThroughService,
        private checklistsService: ChecklistsService,
        private toastService: ToastService,
        private confirmationService: ConfirmationService,
        private organizationService: OrganizationSetupFormService,
        private sidebarService: SidebarService,
        private sidebarViewService: SidebarViewService,
        private subscriptionState: SubscriptionStateService,
        private features: FeatureFlagService
    ) {
        this.currentUser = this.authService.getUser() ?? {} as IUser;
        this.startTour();

        effect(() => {
            const details = this.subscriptionState.subscriptionDetails();
            if (details && !this.setupSidebarDone) {
                this.subscriptionDetails = details;
                this.setUpSidebar();

                const tourType = this.currentUser.productWalkThrough?.find(
                    walkthrough => walkthrough.type === PRODUCT_WALKTHROUGH_TYPE.SETTINGS
                );
                if (!tourType?.status) {
                    let data = { type: PRODUCT_WALKTHROUGH_TYPE.SETTINGS, status: false };
                    this.userService.updateWalkThrough(data).subscribe();
                    setTimeout(() => this.initializeTour(), 100);
                }
            }
        });
    }

    startTour() {
        const tourType = this.currentUser.productWalkThrough?.find(
            walkthrough => walkthrough.type === PRODUCT_WALKTHROUGH_TYPE.SETTINGS
        );
        if (!tourType?.status) {
            this.router.navigate(['settings/organization-setup']);
            // Don't initialize tour here - will be called after sidebar menu is set up
        }
    }


    ngOnChanges(changes: SimpleChanges) {
        this.cd.detectChanges();
    }


    ngOnInit() {

        const details = this.subscriptionState.subscriptionDetails();
        if (details && !this.setupSidebarDone) {
            this.loadSubscriptionDetails();
        }

        // Subscribe to router events
        this.routerSubscription = this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                // Handle route changes if needed
            }
        });

        this.walkThroughSubscription = this.productWalkThroughService.walkThroughReset$.subscribe(type => {
            if (type == PRODUCT_WALKTHROUGH_TYPE.SETTINGS) {
                this.router.navigate(['settings/organization-setup'])
                // Add delay to ensure navigation completes and DOM is ready
                setTimeout(() => this.initializeTour(), 500);
            }
        })
    }

    setUpSidebar() {
        this.setupSidebarMenu();
        this.updateSidebarMenuWithIntegrations();
        this.setupSidebarDone = true;

        // Push the settings view into the shared sidebar
        this.sidebarViewService.pushView({
            id: 'settings',
            title: 'Settings',
            menu: this.sidebarMenu,
            backRoute: '/explore',
            showFooter: false,
        });
    }

    /**
     * Load subscription details from cached state
     * ✅ Uses cached data loaded during authentication - NO API CALL
     */
    loadSubscriptionDetails() {
        const details = this.subscriptionState.subscriptionDetails();
        if (details) {
            this.subscriptionDetails = details;
            // Update sidebar menu with integrations if user qualifies
            this.setUpSidebar();

            // Initialize tour after sidebar menu is set up
            const tourType = this.currentUser.productWalkThrough?.find(
                walkthrough => walkthrough.type === PRODUCT_WALKTHROUGH_TYPE.SETTINGS
            );
            if (!tourType?.status) {
                let data = { type: PRODUCT_WALKTHROUGH_TYPE.SETTINGS, status: false };
                this.userService.updateWalkThrough(data).subscribe();
                // Small delay to ensure DOM is ready
                setTimeout(() => this.initializeTour(), 100);
            }
        }
    }

    /**
     * @deprecated Use loadSubscriptionDetails() - now uses cached data only
     */
    getSubscriptionDetails() {
        this.loadSubscriptionDetails();
    }

    /**
     * Sets up the sidebar menu with base items (called immediately on init)
     * Integrations menu will be added later based on subscription details
     */
    setupSidebarMenu() {
        // Get feature access using the new mapping system
        const documentsAccess = this.features.checkFeature(UI_FEATURE.DOCUMENTS);
        const projectsAccess = this.features.checkFeature(UI_FEATURE.PROJECTS);
        const hasAnyInnDocFeatures = documentsAccess.allowed || projectsAccess.allowed;

        // Check if sidebar should be locked
        const isLocked = this.subscriptionState.isSidebarLocked();

        if (this.currentUser?.workspaceRole && !([SHARE_ACCESS.ADMIN, SHARE_ACCESS.OWNER].includes(this.currentUser.workspaceRole))) {
            const menuItems: ISideBarMenu[] = [];

            // Organization - Always show
            const orgItems: ISideBarMenu['items'] = [
                {
                    label: 'Organization Setup',
                    route: '/settings/organization-setup',
                    svg: SVG.ORGANIZATION_SETUP,
                },
                {
                    label: 'Credit usage',
                    route: '/settings/credit-usage',
                    svg: SVG.CREDIT_USAGE,
                    iconSource: 'svg',
                },
            ];
            if (this.subscriptionState.subscriptions()?.paymentMode !== PAYMENT_MODE.SALES &&
                (this.currentUser.role !== USER_ROLE.MEMBER || hasBillingAccess(this.currentUser))) {
                orgItems.push({
                    label: 'Billing & Invoices',
                    route: '/settings/billing-invoices',
                    svg: SVG.BILLING_PAYMENTS,
                    iconSource: 'svg',
                });
            }
            menuItems.push({ header: 'Organization', items: orgItems });

            // People - Always show
            menuItems.push({
                header: 'People',
                items: [
                    {
                        label: 'Users',
                        route: '/settings/people/users',
                        svg: SVG.PEOPLE_USERS,
                    },
                ]
            });

            // Apply locked state if sidebar is locked
            if (isLocked) {
                this.applyLockedState(menuItems);
            }

            this.sidebarMenu = menuItems;
        }
        else if ((this.currentUser?.role ? [USER_ROLE.ADMIN, USER_ROLE.SUPER_ADMIN].includes(this.currentUser.role) : false) ||
            (this.currentUser?.workspaceRole ? [SHARE_ACCESS.ADMIN, SHARE_ACCESS.OWNER].includes(this.currentUser.workspaceRole) : false) ||
            hasBillingAccess(this.currentUser)
        ) {
            const menuItems: ISideBarMenu[] = [];

            // Organization - Always show
            const orgItems: ISideBarMenu['items'] = [
                {
                    label: 'Organization Setup',
                    route: '/settings/organization-setup',
                    svg: SVG.ORGANIZATION_SETUP,
                },
                {
                    label: 'Credit usage',
                    route: '/settings/credit-usage',
                    svg: SVG.CREDIT_USAGE,
                    iconSource: 'svg',
                },
            ];
            if (this.subscriptionState.orgSubscriptionDetails()?.paymentMode !== PAYMENT_MODE.SALES &&
                (this.currentUser.role !== USER_ROLE.MEMBER || hasBillingAccess(this.currentUser))) {
                orgItems.push({
                    label: 'Billing & Invoices',
                    route: '/settings/billing-invoices',
                    svg: SVG.BILLING_PAYMENTS,
                    iconSource: 'svg',
                });
            }
            menuItems.push({ header: 'Organization', items: orgItems });

            // People - Always show
            menuItems.push({
                header: 'People',
                items: [
                    {
                        label: 'Users',
                        route: '/settings/people/users',
                        svg: SVG.PEOPLE_USERS,
                    },
                    {
                        label: 'Invites',
                        route: '/settings/people/invites',
                        svg: SVG.PEOPLE_INVITES,
                    },
                    {
                        label: 'Requests',
                        route: '/settings/people/requests',
                        svg: SVG.PEOPLE_REQUESTS,
                    }
                ]
            });

            // Tags - Show if has any InnDoc features
            if (hasAnyInnDocFeatures) {
                menuItems.push({
                    header: 'Tag Type',
                    items: [
                        {
                            label: 'Tag Type',
                            route: '/settings/tags',
                            svg: SVG.TAGS,
                        }
                    ]
                });
            }

            // Documents - Show if has documents features
            if (documentsAccess.allowed) {
                menuItems.push({
                    header: 'Documents',
                    items: [
                        {
                            label: 'Document Types',
                            route: '/settings/documents/document-types',
                            svg: SVG.DOCUMENT_TYPES,
                        },
                        {
                            label: 'Status',
                            route: '/settings/documents/status',
                            svg: SVG.DOCUMENT_STATUS,
                        },
                        {
                            label: 'Milestones',
                            route: '/settings/documents/milestones',
                            svg: SVG.DOCUMENT_MILESTONES,
                        },
                        {
                            label: 'Document Tags',
                            route: '/settings/documents/tags',
                            svg: SVG.DOCUMENT_TAGS,
                        }
                    ]
                });
            }

            // Projects - Show if has projects features
            if (projectsAccess.allowed) {
                menuItems.push({
                    header: 'Projects',
                    items: [
                        {
                            label: 'Status',
                            route: '/settings/projects/status',
                            svg: SVG.PROJECT_STATUS,
                        },
                        {
                            label: 'Milestones',
                            route: '/settings/projects/milestones',
                            svg: SVG.PROJECT_MILESTONES,
                        },
                        {
                            label: 'Project Tags',
                            route: '/settings/projects/tags',
                            svg: SVG.PROJECT_TAGS,
                        }
                    ]
                });
            }

            // Repository - Show if has agent or InnDoc features
            if (hasAnyInnDocFeatures) {
                menuItems.push({
                    header: 'Repository',
                    items: [
                        {
                            label: 'Files',
                            route: '/settings/repository/files',
                            svg: SVG.REPOSITORY_FILES,
                        },
                        {
                            label: 'Knowledge Base',
                            route: '/settings/repository/knowledge-base',
                            svg: SVG.REPOSITORY_KNOWLEDGE_BASE,
                        },
                        {
                            label: 'Knowledge Mapping',
                            route: '/settings/repository/knowledge-mapping',
                            svg: SVG.REPOSITORY_KNOWLEDGE_MAPPING,
                        }
                    ]
                });
            }

            // Checklists and Risks - Show if has any InnDoc features
            if (hasAnyInnDocFeatures) {
                menuItems.push({
                    header: 'Library settings',
                    items: [
                        {
                            label: 'Clause Library',
                            route: '/settings/library/clause-library',
                            svg: SVG.CLAUSE_LIBRARY,
                        },
                        {
                            label: 'Template Library',
                            route: '/settings/library/template-library',
                            svg: SVG.TEMPLATE_LIBRARY
                        }
                    ]
                })

                menuItems.push({
                    header: 'Checklists and Risks',
                    items: [
                        {
                            label: 'Checklists',
                            route: '/settings/checklists-and-risks/checklists',
                            svg: SVG.CHECKLISTS,
                        },
                        {
                            label: 'Risks',
                            route: '/settings/checklists-and-risks/risks',
                            svg: SVG.RISKS,
                        }
                    ]
                });
            }

            // Innform Q&A - Show if has any InnDoc features
            if (hasAnyInnDocFeatures) {
                menuItems.push({
                    header: 'Innform Q&A',
                    items: [
                        {
                            label: 'Q&A Bank',
                            route: '/settings/innform/qa-pair',
                            svg: SVG.INNFORM_QA_BANK,
                        },
                        {
                            label: 'Attachments',
                            route: '/settings/innform/attachments',
                            svg: SVG.INNFORM_ATTACHMENTS,
                        }
                    ]
                });
            }

            // Apply locked state if sidebar is locked
            if (isLocked) {
                this.applyLockedState(menuItems);
            }

            this.sidebarMenu = menuItems;
        }
    }

    /**
     * 🔒 Apply locked state to menu items (prevents navigation at route level)
     * Visual locking is handled by CSS (.sidebar-locked class on container)
     */
    private applyLockedState(menuItems: ISideBarMenu[]): void {
        menuItems.forEach(menu => {
            menu.locked = true;
            // Remove routes to prevent navigation (route guard will also catch this)
            if (menu.items) {
                menu.items.forEach(item => {
                    if (item.route) {
                        item.route = '';
                    }
                });
            }
            if (menu.create?.route) {
                menu.create.route = '';
            }
            if (menu.more?.route) {
                menu.more.route = '';
            }
        });
    }

    /**
     * Updates the sidebar menu with integrations section if user qualifies
     * Called after subscription details are loaded
     */
    updateSidebarMenuWithIntegrations() {
        // Check if integrations menu already exists
        const hasIntegrations = this.sidebarMenu.some(menu => menu.header === 'Integrations');

        if (!hasIntegrations) {
            // Check if user has integrations features using new mapping system
            const basicAccess = this.features.checkFeature(UI_FEATURE.INTEGRATIONS_BASIC);
            const premiumAccess = this.features.checkFeature(UI_FEATURE.INTEGRATIONS_PREMIUM);
            const integrationsAccess = { allowed: basicAccess.allowed || premiumAccess.allowed };

            // Only add if user has integration features
            if (integrationsAccess.allowed) {
                this.sidebarMenu.push({
                    header: 'Integrations',
                    items: [
                        {
                            label: 'Integrations',
                            route: '/settings/integrations',
                            svg: SVG.INTEGRATIONS,
                        }
                    ]
                });
                // Update the existing settings view in the sidebar
                this.sidebarViewService.updateViewMenu('settings', [...this.sidebarMenu]);
            }
        }
    }



    initializeTour() {
        const steps = this.generateTourSteps();

        this.driver = driver({
            stageRadius: 14,
            showProgress: true,
            nextBtnText: 'Next',
            prevBtnText: 'Previous',
            onCloseClick: () => this.completeTour(),
            onDestroyed: () => this.completeTour(),
            onPrevClick: () => this.moveToNextStep(-1),
            onNextClick: () => this.moveToNextStep(1),
            popoverClass: 'custom-popover',
            showButtons: ['next', 'previous'],
            steps: steps
        });

        // Start the tour
        setTimeout(() => this.driver.drive(), 500);
    }

    private generateTourSteps() {
        const steps: any[] = [];

        this.sidebarMenu.forEach((menuSection) => {
            if (menuSection.items) {
                menuSection.items.forEach((item) => {
                    // Create tour steps for each menu item
                    if (item.route) {
                        const routePath = item.route.replace('/settings/', '');
                        steps.push({
                            element: `#${this.settingsSidebarId} [href="${item.route}"]`,
                            popover: {
                                title: item.label,
                                description: this.getDescriptionForRoute(routePath),
                                side: 'right',
                                align: 'start'
                            }
                        });
                    }
                });
            }
        });

        return steps;
    }

    private getDescriptionForRoute(route: string): string {
        const descriptions: { [key: string]: string } = {
            'organization-setup': 'Configure the key organizational information to establish a professional presence for effective communication.',
            'credit-usage': 'View your organization\'s credit consumption and usage details.',
            'people/users': 'View and manage existing team members who can access the platform.',
            'people/invites': 'Expand your team effortlessly by inviting new members to join the platform.',
            'people/requests': 'View and manage access requests from users.',
            'tags': 'Click here to manage and refine your tags for better document insights!',
            'documents/document-types': 'View and manage document types for better insights.',
            'documents/status': 'View and manage document status for better tracking.',
            'documents/milestones': 'View and manage document milestones for better tracking.',
            'documents/tags': 'View and manage document tags for better insights.',
            'projects/status': 'View and manage project status for better tracking.',
            'projects/milestones': 'View and manage project milestones for better tracking.',
            'projects/tags': 'View and manage project tags for better insights.',
            'repository/files': 'Upload and manage files for your knowledge base.',
            'repository/knowledge-base': 'Access and manage your knowledge base store.',
            'repository/knowledge-mapping': 'View and manage existing knowledge mappings for your documents.',
            'checklists-and-risks/checklists': 'Create and manage checklists for your documents.',
            'checklists-and-risks/risks': 'Assess and manage risks for your documents.',
            'innform/qa-pair': 'View and manage existing Q&A pairs for your documents.',
            'innform/attachments': 'View and manage existing attachments for your documents.',
            'library/clause-library': 'Manage and organize your clause library.',
            'library/template-library': 'View and manage your template library for document creation.',
            'integrations': 'Manage your third-party integrations and connections.'
        };
        return descriptions[route] || 'Manage your settings here.';
    }



    completeTour() {
        let data = { type: PRODUCT_WALKTHROUGH_TYPE.SETTINGS, status: true };
        this.userService.updateWalkThrough(data).subscribe(() => {
        });
    }

    moveToNextStep(direction: number) {
        // Handle navigation based on direction
        if (direction === 1) {
            this.driver.moveNext();
        } else if (direction === -1) {
            this.driver.movePrevious();
        }
    }

    ngOnDestroy() {
        this.sidebarViewService.popView();

        // Clean up the subscription to prevent memory leaks
        if (this.routerSubscription)
            this.routerSubscription.unsubscribe();
        if (this.walkThroughSubscription)
            this.walkThroughSubscription.unsubscribe();
        if (this.driver)
            this.driver.destroy();
    }

    isActive(routerLink: string): boolean {
        return this.router.url.includes(routerLink);
    }

    goBack() {
        this.router.navigate(['']);
    }

    @HostListener('window:popstate', ['$event'])
    onPopState(event: Event) {
        this.router.navigate(['']);
    }

    openUploadExcelDialog() {
        this.uploadExcelDialogVisible = true
    }

    downloadSampleExcel() {
        this.checklistsService.downloadSampleExcel().subscribe(res => {
            if (res.success && res.data) {
                const blob = new Blob([new Uint8Array(res.data.data)], { type: 'vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8' });
                saveAs(blob, 'Checklists Sample Excel.xlsx');
            }
        })
    }

    downloadExistingExcel() {
        this.checklistsService.downloadExistingExcel().subscribe(res => {
            if (res.success && res.data) {
                const blob = new Blob([new Uint8Array(res.data.data)], { type: 'vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8' });
                saveAs(blob, 'Checklists Excel.xlsx');
            }
        })
    }

    uploadExcelDocument(event: {
        file: File,
        type: UPLOAD_TYPE
    }) {
        const formData = new FormData()
        formData.append('file', event.file)
        formData.append('type', event.type)
        this.checklistsService.uploadExcel(formData).subscribe(res => {
            if (res.success) {
                if (res.data.buffer) {
                    const blob = new Blob([new Uint8Array(res.data.buffer.data)], { type: 'vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=utf-8' });
                    saveAs(blob, 'Checklists Errors Excel.xlsx');
                }
                else {
                    this.toastService.success("Excel uploaded successfully")
                }
                // Reload the page after 500ms to reflect the changes in the sub components 
                setTimeout(() => {
                    window.location.reload();
                }, 500)
            }
        })
    }

    uploadExcel(event: {
        file: File,
        type: UPLOAD_TYPE
    }) {
        if (event.type == UPLOAD_TYPE.CREATE) {
            this.confirmationService.confirm({
                header: 'Confirmation',
                message: `Uploading in create case will delete the existing data from all document types. Do you want to proceed?`,
                accept: () => {
                    this.uploadExcelDocument(event);
                },
                reject: () => {

                }
            });
        } else if (event.type == UPLOAD_TYPE.UPDATE) {
            this.uploadExcelDocument(event);
        }
    }

    onExcelDialogClose() {
        this.uploadExcelDialogVisible = false;
    }

    handleConfirmAccept(message: any) {
        // Call the accept callback if it exists
        if (message.accept) {
            message.accept();
        }
        // Hide the dialog
        this.confirmationService.close();
    }

    handleConfirmReject(message: any) {
        // Call the reject callback if it exists
        if (message.reject) {
            message.reject();
        }
        // Hide the dialog
        this.confirmationService.close();
    }


}
